import React, { useState, ReactNode, useId } from 'react';
import styled from 'styled-components';
import { GhostButton } from '../theme/Button/Button';

type HeadingTag = 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface TabsProps {
  asBtn?: boolean;
  centered?: boolean;
  children?: ReactNode[];
  classNamePrefix?: string;
  labelAs?: HeadingTag;
  /** A second class on every tab label, for a caller whose labels answer to two names. */
  labelClassName?: string;
  onTabClick?: (index: number) => void;
  selectedIndex?: number;
  tabsLabel: string[];
}

interface TabItemProps {
  readonly ariaControls: string;
  readonly asBtn?: boolean;
  readonly classNamePrefix?: string;
  readonly clickable: boolean;
  readonly index: number;
  readonly label: string;
  readonly labelClassName?: string;
  readonly selected: boolean;
  readonly labelAs?: HeadingTag;
  readonly onTabClick: (index: number) => void;
}

const TabItem = ({
  ariaControls,
  asBtn,
  classNamePrefix = '',
  clickable,
  index,
  label,
  labelAs,
  labelClassName,
  onTabClick,
  selected,
}: TabItemProps) => {
  const handleClick = () => onTabClick(index);
  const className = [`${classNamePrefix}tab-label`, labelClassName]
    .filter(Boolean)
    .join(' ');
  const content = labelAs ? (
    <LabelHeading as={labelAs}>{label}</LabelHeading>
  ) : (
    label
  );

  if (asBtn) {
    return (
      <TabLabelBtn
        aria-controls={ariaControls}
        aria-selected={selected}
        className={className}
        onClick={handleClick}
        role="tab"
        selected={selected}
      >
        <TabLabelText selected={selected}>{content}</TabLabelText>
      </TabLabelBtn>
    );
  }

  return (
    <TabLabel
      aria-controls={ariaControls}
      aria-selected={selected}
      className={className}
      clickable={clickable}
      onClick={handleClick}
      role="tab"
      selected={selected}
    >
      {content}
    </TabLabel>
  );
};

const Tabs = ({
  asBtn,
  centered,
  children,
  classNamePrefix = '',
  labelAs,
  labelClassName,
  onTabClick,
  selectedIndex,
  tabsLabel,
}: TabsProps) => {
  const [internalIndex, setInternalIndex] = useState(0);
  const isControlled = selectedIndex !== undefined;
  const selectedTabIndex = isControlled ? selectedIndex : internalIndex;

  const id = useId();

  if (tabsLabel.length === 0) {
    return null;
  }

  const handleTabClick = (index: number) => {
    if (!isControlled) setInternalIndex(index);
    onTabClick?.(index);
  };

  return (
    <Wrapper>
      <TabsHeader role="tablist" centered={centered}>
        {tabsLabel.map((label, index) => (
          <TabItem
            ariaControls={id}
            asBtn={asBtn}
            classNamePrefix={classNamePrefix}
            clickable={index !== selectedTabIndex && tabsLabel.length > 1}
            index={index}
            key={label}
            label={label}
            labelAs={labelAs}
            labelClassName={labelClassName}
            onTabClick={handleTabClick}
            selected={index === selectedTabIndex}
          />
        ))}
      </TabsHeader>
      {children && (
        <Content role="tabpanel" id={id}>
          {tabsLabel.length === 1
            ? children[0] || children
            : children[selectedTabIndex] || null}
        </Content>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
`;

const TabsHeader = styled.div<{ centered?: boolean }>`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 24px;
  height: 40px;
  justify-content: ${({ centered }) => (centered ? 'center' : 'flex-start')};
`;

const TabLabel = styled.span<{
  clickable: boolean;
  selected: boolean;
}>`
  /* skip css namespace */
  user-select: none;
  margin: 0;
  padding-bottom: 6px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 900;
  border-bottom: ${({ selected }) =>
    selected
      ? 'solid 0.5px var(--way-colors-borderColor)'
      : 'solid 0.5px transparent'};
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  color: ${({ selected, theme }) =>
    `${theme.colors.primaryTextColor}${selected ? 'ff' : '99'}`};
`;

const TabLabelBtn = styled(GhostButton)<{
  selected: boolean;
}>`
  margin: 0;
  height: 32px;
  padding: 0;
  min-width: 32px;
  font-family: inherit;
  background: none;
  transition: color 120ms ease;

  /* Pull the unselected label down to a brand-aware mid-grey so the selected tab reads as
     noticeably darker (GhostButton's default 80 vs 100 is too subtle). */
  color: ${({ selected }) =>
    selected
      ? 'var(--way-colors-contrastColorShades-100)'
      : 'var(--way-colors-contrastColorShades-50)'};

  &:hover,
  &:active,
  &:focus,
  &:focus-visible {
    background: none;
    color: var(--way-colors-contrastColorShades-100);
  }
`;

const Content = styled.div`
  padding: 16px;
  padding-left: 0px;
  min-height: 50px;
`;

const LabelHeading = styled.span`
  color: inherit;
  display: contents;
  font: inherit;
`;

/* Inline-block wrapper around the tab label text. The transparent border-bottom on the
   unselected state acts as a spacer so swapping in the visible underline on selection doesn't
   shift the text vertically. Sits inside TabLabelBtn — the underline therefore only spans the
   text width, not the full button (including its padding). */
const TabLabelText = styled.span<{ selected: boolean }>`
  display: inline-block;
  padding-bottom: 2px;
  line-height: 1;
  border-bottom: 0.5px solid
    ${({ selected }) =>
      selected ? 'var(--way-colors-contrastColorShades-100)' : 'transparent'};
`;

export default Tabs;
